import ApiHelper from "common/api-helper";
import Cache from 'common/simplecache';

/**
 * ログレベル(Microsoft.Extensions.Logging.LogLevelと同じ値)
 */
export enum LogLevel {
    Trace = 0,
    Debug = 1,
    Information = 2,
    Warning = 3,
    Error = 4,
    Critical = 5,
    None = 6
}

export enum EnumTangetuRuikeiKubun {
    Tangetu = "1",
    Ruikei = "2"
}

var CacheKeys = {
    //Gaicyusaki: 'GAICYUSAKI'
};

var Calling = {};
const sleep = (msec) => {
    return new Promise(resolve => {
        setTimeout(() => {
            resolve(true)
        }, msec)
    })
}

class CallWithCache {
    static async call<T>(key: string, func: () => Promise<T>): Promise<T> {
        // 現在取得中の場合、少し待つ
        let limit = 10;
        while (Calling[key]) {
            await sleep(100);
            if (limit-- == 0) {
                delete Calling[key];
            }
        }

        // キャッシュにあればキャッシュから返す
        let cacheItems = Cache.get(key);
        if (cacheItems != null) {
            return cacheItems;
        }

        // 呼び出して結果をキャッシュして返す
        // 画面呼び出し時に同じマスタを2回も3回も持ってこないように、程度の用途なので、時間は10秒に設定する
        Calling[key] = true;
        try {
            let items = await func();
            Cache.set(key, items, 10);
            return items;
        } finally {
            delete Calling[key];
        }
    }
}

if (__IS_DEBUG__) {
    console.log('is debug');
}

export class Logger {
    static getLevelName(logLevel: LogLevel) {
        switch (logLevel) {
            case LogLevel.Trace: return 'TRACE';
            case LogLevel.Debug: return 'DEBUG';
            case LogLevel.Information: return 'INFOMATION';
            case LogLevel.Warning: return 'WARNING';
            case LogLevel.Error: return 'ERROR';
            case LogLevel.Critical: return 'CRITICAL';
            case LogLevel.None: return 'NONE';
        }
    }
    static async log(logLevel: LogLevel, message: object | string) {
        if (logLevel < __LOG_LEVEL__) {
            return;
        }
        if (logLevel == LogLevel.None) {
            return;
        }

        var _message = message.toString();
        if (message.constructor == Error) {
            _message = (message as Error).message;
        } else if (typeof message === 'object') {
            _message = message.toString();
        }

        // エラー以上はサーバーにも記録する
        if (logLevel >= LogLevel.Error) {
            await LogService.addLogAsync(logLevel, _message);
        }

        // Debug以下はコンソールに出力する
        if (logLevel <= LogLevel.Debug) {
            console.log(Logger.getLevelName(logLevel) + ': ', message);
        }
    }
    static async logCritical(message: string) {
        Logger.log(LogLevel.Critical, message);
    }
    static async logDebug(message: any) {
        Logger.log(LogLevel.Debug, message);
    }
    static async logError(message: string) {
        Logger.log(LogLevel.Error, message);
    }
    static async logInformation(message: string) {
        Logger.log(LogLevel.Information, message);
    }
    static async logTrace(message: string) {
        Logger.log(LogLevel.Trace, message);
    }
    static async logWarning(message: string) {
        Logger.log(LogLevel.Warning, message);
    }
}

class LogService {
    static async addLogAsync(logLevel: LogLevel, message: string) {
        try {
            return (await ApiHelper.post("Log/Logging", {
                logLevel: logLevel,
                message: message
            })) || [];
        } catch (e) {
            throw e;
        }
    }
}

/**
 * メソッドの実行時、エラーが出ればlogDebugしたのち、そのエラーをthrowするように、メソッドをラップします。
 * @returns 
 */
const logDebug = function () {
    return function (target: any, key: any, desc: PropertyDescriptor) {

        const method = desc.value
        desc.value = function (...args) {
            try {
                return method(...args)
            } catch (e) {
                Logger.logDebug(e.message);
                throw e;
            }
        }
    }
}

/**
 * メソッドの実行時、キャッシュを使用し、すでに取得されていたデータならキャッシュから返します。
 * エラーが出ればlogDebugしたのち、そのエラーをthrowするように、メソッドをラップします。
 * @returns 
 */
const useCache = function (controllerName: string, methodName: string) {
    return function (target: any, key: string, desc: PropertyDescriptor) {

        // もとのメソッドを退避しておいて
        const originalMethod = desc.value

        //メソッドを書き換える
        desc.value = function (...args) {
            try {

                const method = async () => {
                    return await originalMethod(...args)
                }

                const key = `${controllerName}__${methodName}__` + JSON.stringify(args);
                const result = CallWithCache.call(key, method)
                return result
            } catch (e) {
                Logger.logDebug(e.message);
                throw e;
            }
        }
    }
}

/**
 * メソッドの実行時、指定されたcontrollerをもつキャッシュをすべてクリアします。
 * @returns 
 */
const clearCache = function (controllerName: string) {
    return function (target: any, key: string, desc: PropertyDescriptor) {

        // もとのメソッドを退避しておいて
        const originalMethod = desc.value

        //メソッドを書き換える
        desc.value = function (...args) {
            try {

                Cache.clearStartsWith(`${controllerName}__`)
                return originalMethod(...args)

            } catch (e) {
                Logger.logDebug(e.message);
                throw e;
            }
        }
    }
}

export class OptionService {
    static async getValueAsync(key: string) {
        try {
            return await ApiHelper.get("Option", { 'key': key });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async insertOrUpdateOptionAsync(option: API.InsertOrUpdateOptionCommandRequest) {
        try {
            return (await ApiHelper.post("Option/InsertOrUpdateOption", {
                'Key': option.key,
                'Value': option.value
            }));
        } catch (e) {
            throw e;
        }
    }
}

export class UserService {
    static async getUsersForLoginAsync() {
        try {
            return await ApiHelper.getNoAuth("Users/GetUsersForLogin");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getUsersAsync() {
        try {
            return await ApiHelper.get("Users/GetAllUser");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getUserListAsync(request: API.GetUsersListRequest): Promise<API.GetUserListResponse> {
        try {
            return await ApiHelper.get("Users/GetUsers", request);
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getUserByUserIdAsync(userId: string) {
        try {
            return await ApiHelper.get("Users/GetUserByUserId", { 'userId': userId });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteUserAsync(userId: string) {
        try {
            return (await ApiHelper.post("Users/DeleteByUserId", {
                ユーザーID: userId
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addUserAsync(user: API.UserModel) {
        try {
            return (await ApiHelper.post("Users/AddUser", {
                Item: user
            }));
        } catch (e) {
            throw e;
        }
    }
    static async updateUserAsync(user: API.UserModel) {
        try {
            return (await ApiHelper.post("Users/UpdateUser", {
                Item: user
            }));
        } catch (e) {
            throw e;
        }
    }
    static async updateUserEMailAsync(request: API.UpdateUserEMailCommandRequest) {
        try {
            return (await ApiHelper.post("Users/UpdateUserEMail", request));
        } catch (e) {
            throw e;
        }
    }
}

export class TantouService {
    static async getTantouListAsync() {
        try {
            return await ApiHelper.get("Tantou/GetTantouList");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getTantouByCodeAsync(code: string): Promise<any> {
        try {
            return await ApiHelper.get("Tantou/GetTantouByCode", {
                'code': code
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteTantouByCodeAsync(code: string) {
        try {
            return (await ApiHelper.post("Tantou/DeleteByCode", {
                担当者コード: code
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addTantouAsync(item: any) {
        try {
            return (await ApiHelper.post("Tantou/AddTantou", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
    static async updateTantouAsync(item: any) {
        try {
            return (await ApiHelper.post("Tantou/UpdateTantou", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
}

export class MeisyoService {

    static async getUserLevelAsync(): Promise<API.MeisyoModel[]> {
        try {
            return (await ApiHelper.get("Meisyo/GetMeisyoListByKubun", { 'kubun': "USER_LEVEL" }));
        } catch (e) {
            Logger.logError(e.message);
            throw e;
        }
    }
    static async getGakubuAsync(): Promise<API.MeisyoModel[]> {
        try {
            return (await ApiHelper.get("Meisyo/GetMeisyoListByKubun", { 'kubun': "Gakubu" }));
        } catch (e) {
            Logger.logError(e.message);
            throw e;
        }
    }

    static async getMeisyoListAsync(): Promise<API.MeisyoModel[]> {
        try {
            return await ApiHelper.get("Meisyo/GetMeisyoList");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async getMeisyoByKubunAndCDAsync(kubun: string, meisyoCD: string): Promise<API.MeisyoModel> {
        try {
            return await ApiHelper.get("Meisyo/GetMeisyoByKubunAndCD", {
                "kubun": kubun,
                "meisyoCD": meisyoCD
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async deleteMeisyoAsync(kubun: string, meisyoCD: string) {
        try {
            return (await ApiHelper.post("Meisyo/DeleteMeisyoByKubunAndCD", {
                "名称区分": kubun,
                "名称コード": meisyoCD
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addMeisyoAsync(meisyo: API.MeisyoModel) {
        try {
            return await ApiHelper.post("Meisyo/AddMeisyo", {
                Item: meisyo
            });
        } catch (e) {
            throw e;
        }
    }
    static async updateMeisyoAsync(meisyo: API.MeisyoModel) {
        try {
            return await ApiHelper.post("Meisyo/UpdateMeisyo", {
                Item: meisyo
            });
        } catch (e) {
            throw e;
        }
    }
}

export class UploadFileService {
    static async uploadAsync(file) {
        try {
            let formData = new FormData();
            formData.append('file', file, file.name);
            return (await ApiHelper.post("UploadFile/Upload", formData));
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    @logDebug()
    static async uploadAzureAsync(file: File): Promise<API.UploadFileResponse> {
        let formData = new FormData();
        formData.append('file', file, file.name);
        return await ApiHelper.post("UploadFile/UploadAzure", formData);
    }
    static async deleteAsync(id) {
        try {
            return (await ApiHelper.post("UploadFile/Delete", {
                'id': id
            }));
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async showAsync(id) {
        try {
            return (await ApiHelper.getBinary("UploadFile/Show", {
                'id': id
            }));
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
}

export class ZeirituService {

    /**
     * 税率はほとんど変化しない＆大した量ではないので、
     * 必要になったタイミングで全部取得して保持しておいて
     * そのあとは、保持した税率を使って計算する
     */
    static _ZeirituList: Array<any> = null;

    /**
     * 指定した日付、軽減税率対象区分の税率を取得する
     * @param targetDate
     * @param keigenZeirituTaisyoKubun
     */
    static async getZeirituAsync(targetDate: string, keigenZeirituTaisyoKubun: number): Promise<number> {
        try {
            if (ZeirituService._ZeirituList == null) {
                ZeirituService._ZeirituList = await ZeirituService.getZeirituListAsync();
            }

            let zeiritu = 0;
            const _zeirituMST = ZeirituService._ZeirituList
                .filter(x => x.開始日付 <= targetDate
                    && x.軽減税率対象区分 == keigenZeirituTaisyoKubun)
                .sort((a, b) => a.開始日付 < b.開始日付 ? -1 : 1)
                .pop();
            if (_zeirituMST && _zeirituMST.税率) {
                zeiritu = _zeirituMST.税率;
            }

            return zeiritu;
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getZeirituListAsync() {
        try {
            return (await ApiHelper.get("Zeiritu/GetZeirituList"));
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async getZeirituByIDAsync(zeirituID: number) {
        try {
            return await ApiHelper.get("Zeiritu/GetZeirituByID", {
                zeirituID: zeirituID
            })
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async addZeirituAsync(zeiritu: any) {
        try {
            return await ApiHelper.post("Zeiritu/AddZeiritu", {
                Item: zeiritu
            });
        } catch (e) {
            throw e;
        }
    }


    static async updateZeirituAsync(zeiritu: any) {
        try {
            return await ApiHelper.post("Zeiritu/UpdateZeiritu", {
                Item: zeiritu
            });
        } catch (e) {
            throw e;
        }
    }

    static async deleteZeirituAsync(zeirituId: number, stDate: string) {
        try {
            return (await ApiHelper.post("Zeiritu/DeleteByDateAndKeigenKubun", {
                ZeirituID: zeirituId,
                開始日付: stDate
            }));
        } catch (e) {
            throw e;
        }
    }
}

export class PostalCodeService {
    static async getDataByPostalCode(postalCode: string): Promise<API.PostalCodeModel | null> {
        try {
            return (await ApiHelper.get("PostalCode/GetByPostalCode", {
                "PostalCode": postalCode
            }));
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
}

export class YakusyokuService {
    @logDebug()
    static async getYakusyokuListAsync() {
        return await ApiHelper.get("Yakusyoku/GetYakusyokuList");
    }
    static async getYakusyokuSeachListAsync() {
        return await ApiHelper.get("Yakusyoku/GetYakusyokuSeachList");
    }

    static async getYakusyokuByCDAsync(yakusyokuCD: string) {
        try {
            return await ApiHelper.get("Yakusyoku/GetYakusyokuByCD", {
                "yakusyokuCD": yakusyokuCD
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async deleteYakusyokuAsync(yakusyokuCD: string) {
        try {
            return (await ApiHelper.post("Yakusyoku/DeleteYakusyokuByCD", {
                "役職区分コード": yakusyokuCD
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addYakusyokuAsync(yakusyoku: API.YakusyokuModel) {
        try {
            return await ApiHelper.post("Yakusyoku/AddYakusyoku", {
                Item: yakusyoku
            });
        } catch (e) {
            throw e;
        }
    }
    static async updateYakusyokuAsync(yakusyoku: API.YakusyokuModel) {
        try {
            return await ApiHelper.post("Yakusyoku/UpdateYakusyoku", {
                Item: yakusyoku
            });
        } catch (e) {
            throw e;
        }
    }

}

export class SyokusyuService {
    static async getSyokusyuListAsync(): Promise<API.SyokusyuModel[]> {
        try {
            return await ApiHelper.get("Syokusyu/GetSyokusyuList");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async GetSyokusyuSearchList(): Promise<API.SyokusyuModel[]> {
        try {
            return await ApiHelper.get("Syokusyu/GetSyokusyuSearchList");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getSyokusyuByCDAsync(syokusyuCD: string) {
        try {
            return await ApiHelper.get("Syokusyu/GetSyokusyuByCD", {
                "SyokusyuCD": syokusyuCD
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async deleteSyokusyuAsync(syokusyuCD: string) {
        try {
            return (await ApiHelper.post("Syokusyu/DeleteSyokusyuByCD", {
                "職種コード": syokusyuCD
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addSyokusyuAsync(syokusyu: API.SyokusyuModel) {
        try {
            return await ApiHelper.post("Syokusyu/AddSyokusyu", {
                Item: syokusyu
            });
        } catch (e) {
            throw e;
        }
    }
    static async updateSyokusyuAsync(syokusyu: API.SyokusyuModel) {
        try {
            return await ApiHelper.post("Syokusyu/UpdateSyokusyu", {
                Item: syokusyu
            });
        } catch (e) {
            throw e;
        }
    }
}

export class GyousyuService {
    @logDebug()
    static async getGyousyuListAsync(): Promise<API.GyousyuModel[]> {
        return await ApiHelper.get("Gyousyu/GetGyousyuList");
    }
    static async getGyousyuSearchListAsync(): Promise<API.GyousyuModel[]> {
        return await ApiHelper.get("Gyousyu/GetGyousyuSearchList");
    }
    static async getGyousyuByCDAsync(gyousyuCD: string) {
        try {
            return await ApiHelper.get("Gyousyu/GetGyousyuByCD", {
                "GyousyuCD": gyousyuCD
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteGyousyuAsync(gyousyuCD: string) {
        try {
            return (await ApiHelper.post("Gyousyu/DeleteGyousyuByCD", {
                "業種コード": gyousyuCD
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addGyousyuAsync(gyousyu: API.GyousyuModel) {
        try {
            return await ApiHelper.post("Gyousyu/AddGyousyu", {
                Item: gyousyu
            });
        } catch (e) {
            throw e;
        }
    }
    static async updateGyousyuAsync(gyousyu: API.GyousyuModel) {
        try {
            return await ApiHelper.post("Gyousyu/UpdateGyousyu", {
                Item: gyousyu
            });
        } catch (e) {
            throw e;
        }
    }
}

export class TikuService {
    @logDebug()
    static async getTikuListAsync(): Promise<API.TikuModel[]> {
        return await ApiHelper.get("Tiku/GetTikuList");
    }
    static async getTikuSeachListAsync(): Promise<API.TikuModel[]> {
        return await ApiHelper.get("Tiku/GetTikuSeachList");
    }
    static async getTikuByCDAsync(tikuCD: string) {
        try {
            return await ApiHelper.get("Tiku/GetTikuByCD", {
                "TikuCD": tikuCD
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteTikuAsync(tikuCD: string) {
        try {
            return (await ApiHelper.post("Tiku/DeleteTikuByCD", {
                "地区コード": tikuCD
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addTikuAsync(tiku: API.TikuModel) {
        try {
            return await ApiHelper.post("Tiku/AddTiku", {
                Item: tiku
            });
        } catch (e) {
            throw e;
        }
    }
    static async updateTikuAsync(tiku: API.TikuModel) {
        try {
            return await ApiHelper.post("Tiku/UpdateTiku", {
                Item: tiku
            });
        } catch (e) {
            throw e;
        }
    }

}

export class KyujinGakkaService {
    @logDebug()
    static async getKyujinGakkaListAsync(): Promise<API.KyujinGakkaModel[]> {
        return await ApiHelper.get("KyujinGakka/GetKyujinGakkaList");
    }
    static async getKyujinGakkaSeachListAsync(): Promise<API.KyujinGakkaModel[]> {
        return await ApiHelper.get("KyujinGakka/GetKyujinGakkaSearchList");
    }
    static async getKyujinGakkaByCDAsync(kyujinGakkaCD: string) {
        try {
            return await ApiHelper.get("KyujinGakka/GetKyujinGakkaByCD", {
                "kyujinGakkaCD": kyujinGakkaCD
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteKyujinGakkaAsync(gyousyuCD: string) {
        try {
            return (await ApiHelper.post("KyujinGakka/DeleteKyujinGakkaByCD", {
                "求人学科コード": gyousyuCD
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addKyujinGakkaAsync(kyujinGakka: API.KyujinGakkaModel) {
        try {
            return await ApiHelper.post("KyujinGakka/AddKyujinGakka", {
                Item: kyujinGakka
            });
        } catch (e) {
            throw e;
        }
    }
    static async updateKyujinGakkaAsync(kyujinGakka: API.KyujinGakkaModel) {
        try {
            return await ApiHelper.post("KyujinGakka/UpdateKyujinGakka", {
                Item: kyujinGakka
            });
        } catch (e) {
            throw e;
        }
    }

}

export class KyujinGyousyuService {
    @logDebug()
    static async getKyujinGyousyuListAsync(): Promise<API.KyujinGyousyuModel[]> {
        return await ApiHelper.get("KyujinGyousyu/GetKyujinGyousyuList");
    }
    static async getKyujinGyousyuSeachListAsync(): Promise<API.KyujinGyousyuModel[]> {
        return await ApiHelper.get("KyujinGyousyu/GetKyujinGyousyuSeachList");
    }
    static async getKyujinGyousyuByCDAsync(kyujinGyousyuCD: string) {
        try {
            return await ApiHelper.get("KyujinGyousyu/GetKyujinGyousyuByCD", {
                "KyujinGyousyuCD": kyujinGyousyuCD
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteKyujinGyousyuAsync(gyousyuCD: string) {
        try {
            return (await ApiHelper.post("KyujinGyousyu/DeleteKyujinGyousyuByCD", {
                "求人業種コード": gyousyuCD
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addKyujinGyousyuAsync(kyujinGyousyu: API.KyujinGyousyuModel) {
        try {
            return await ApiHelper.post("KyujinGyousyu/AddKyujinGyousyu", {
                Item: kyujinGyousyu
            });
        } catch (e) {
            throw e;
        }
    }
    static async updateKyujinGyousyuAsync(kyujinGyousyu: API.KyujinGyousyuModel) {
        try {
            return await ApiHelper.post("KyujinGyousyu/UpdateKyujinGyousyu", {
                Item: kyujinGyousyu
            });
        } catch (e) {
            throw e;
        }
    }

}

export class KyujinJiyuKoumokuService {
    static async getKyujinJiyuKoumokuListAsync(): Promise<API.KyujinJiyuKoumokuModel[]> {
        try {
            return await ApiHelper.get("KyujinJiyuKoumoku/GetKyujinJiyuKoumokuList");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getKyujinJiyuKoumokuByCDAsync(JiyuKumokuCD: string, KoumokuSetteiCD): Promise<API.KyujinJiyuKoumokuModel> {
        try {
            return await ApiHelper.get("KyujinJiyuKoumoku/GetKyujinJiyuKoumokuByCD", {
                "JiyuKoumokuCD": JiyuKumokuCD,
                "KoumokuSetteiCD": KoumokuSetteiCD
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteKyujinJiyuKoumokuAsync(JiyuKoumokuCD: string, JiyuKoumokuSetteiCD) {
        try {
            return (await ApiHelper.post("KyujinJiyuKoumoku/DeleteKyujinJiyuKoumokuByCD", {
                "求人自由項目コード": JiyuKoumokuCD,
                "求人自由項目設定コード": JiyuKoumokuSetteiCD
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addKyujinJiyuKoumokuAsync(kyujinJiyuKoumoku: API.KyujinJiyuKoumokuModel) {
        try {
            return await ApiHelper.post("KyujinJiyuKoumoku/AddKyujinJiyuKoumoku", {
                Item: kyujinJiyuKoumoku
            });
        } catch (e) {
            throw e;
        }
    }
    static async updateKyujinJiyuKoumokuAsync(kyujinJiyuKoumoku: API.KyujinJiyuKoumokuModel) {
        try {
            return await ApiHelper.post("KyujinJiyuKoumoku/UpdateKyujinJiyuKoumoku", {
                Item: kyujinJiyuKoumoku
            });
        } catch (e) {
            throw e;
        }
    }

}

export class KyujinJiyuKoumokumeiService {
    @logDebug()
    static async getKyujinJiyuKoumokumeiListAsync(): Promise<API.KyujinJiyuKoumokumeiModel[]> {
        return await ApiHelper.get("KyujinJiyuKoumokumei/GetKyujinJiyukoumokumeiList");
    }
    static async getKyujinJiyuKoumokumeiByCDAsync(koumokuCode: string) {
        try {
            return await ApiHelper.get("KyujinJiyuKoumokumei/GetKyujinJiyukoumokumeiByNo", {
                "kyujinJiyukoumokumeiCode": koumokuCode
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteKyujinJiyuKoumokumeiAsync(koumokuCode: string) {
        try {
            return (await ApiHelper.post("KyujinJiyuKoumokumei/DeleteKyujinJiyukoumokumeiByNo", {
                "求人自由項目コード": koumokuCode
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addKyujinJiyuKoumokumeiAsync(kyujinJiyuKoumokumei: API.KyujinJiyuKoumokumeiModel) {
        try {
            return await ApiHelper.post("KyujinJiyuKoumokumei/AddKyujinJiyukoumokumei", {
                Item: kyujinJiyuKoumokumei
            });
        } catch (e) {
            throw e;
        }
    }
    static async updateKyujinJiyuKoumokumeiAsync(kyujinJiyuKoumokumei: API.KyujinJiyuKoumokumeiModel) {
        try {
            return await ApiHelper.post("KyujinJiyuKoumokumei/UpdateKyujinJiyukoumokumei", {
                Item: kyujinJiyuKoumokumei
            });
        } catch (e) {
            throw e;
        }
    }

}

export class KyujinJiyuKoumokuFlgService {
    @logDebug()
    static async getKyujinJiyuKoumokuFlgListAsync(): Promise<API.KyujinJiyuKoumokuFlgModel[]> {
        return await ApiHelper.get("KyujinJiyuKoumokuFlg/GetKyujinJiyukoumokuFlgList");
    }
    static async getKyujinJiyuKoumokuFlgByNoAsync(koumokuFlgCode: string): Promise<API.KyujinJiyuKoumokuFlgModel> {
        try {
            return await ApiHelper.get("KyujinJiyuKoumokuFlg/GetKyujinJiyukoumokuFlgByNo", {
                "kyujinJiyukoumokuFlgCode": koumokuFlgCode
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteKyujinJiyuKoumokuFlgAsync(koumokuFlgCode: string) {
        try {
            return (await ApiHelper.post("KyujinJiyuKoumokuFlg/DeleteKyujinJiyukoumokuFlgByNo", {
                "求人自由項目フラグコード": koumokuFlgCode
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addKyujinJiyuKoumokuFlgAsync(kyujinJiyuKoumokuFlg: API.KyujinJiyuKoumokuFlgModel) {
        try {
            return await ApiHelper.post("KyujinJiyuKoumokuFlg/AddKyujinJiyukoumokuFlg", {
                Item: kyujinJiyuKoumokuFlg
            });
        } catch (e) {
            throw e;
        }
    }
    static async updateKyujinJiyuKoumokuFlgAsync(kyujinJiyuKoumokuFlg: API.KyujinJiyuKoumokuFlgModel) {
        try {
            return await ApiHelper.post("KyujinJiyuKoumokuFlg/UpdateKyujinJiyukoumokuFlg", {
                Item: kyujinJiyuKoumokuFlg
            });
        } catch (e) {
            throw e;
        }
    }

}

export class KyujinSyokusyuService {
    @logDebug()
    static async getKyujinSyokusyuListAsync(): Promise<API.KyujinSyokusyuModel[]> {
        return await ApiHelper.get("KyujinSyokusyu/GetKyujinSyokusyuList");
    }
    static async getKyujinSyokusyuSearchListAsync(): Promise<API.KyujinSyokusyuModel[]> {
        return await ApiHelper.get("KyujinSyokusyu/GetKyujinSyokusyuSeachList");
    }
    static async getKyujinSyokusyuByCDAsync(kyujinSyokusyuCD: string) {
        try {
            return await ApiHelper.get("KyujinSyokusyu/GetKyujinSyokusyuByCD", {
                "kyujinSyokusyuCD": kyujinSyokusyuCD
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteKyujinSyokusyuAsync(kyujinSyokusyuCD: string) {
        try {
            return (await ApiHelper.post("KyujinSyokusyu/DeleteKyujinSyokusyuByCD", {
                "求人職種コード": kyujinSyokusyuCD
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addKyujinSyokusyuAsync(kyujinSyokusyu: API.KyujinSyokusyuModel) {
        try {
            return await ApiHelper.post("KyujinSyokusyu/AddKyujinSyokusyu", {
                Item: kyujinSyokusyu
            });
        } catch (e) {
            throw e;
        }
    }
    static async updateKyujinSyokusyuAsync(kyujinSyokusyu: API.KyujinSyokusyuModel) {
        try {
            return await ApiHelper.post("KyujinSyokusyu/UpdateKyujinSyokusyu", {
                Item: kyujinSyokusyu
            });
        } catch (e) {
            throw e;
        }
    }

}

export class KyujinService {

    static async getKyujinListAsync(
        request: Partial<API.GetKyujinListRequest>
    ): Promise<API.GetKyujinListResponse> {
        try {
            return await ApiHelper.get("Kyujin/GetKyujinList", request);
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getKyujinJyokyoListAsync(
        request: Partial<API.GetKyujinListRequest>
    ): Promise<API.GetKyujinListResponse> {
        try {
            return await ApiHelper.get("Kyujin/GetKyujinJyokyoList", request);
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async getKyujinListForExcelAsync(
        request: Partial<API.GetKyujinListForExcelRequest>
    ) {
        try {
            return await ApiHelper.getBinary<ArrayBuffer>("Kyujin/GetKyujinListForExcel", request);
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async getKyujinJyokyoListForExcelAsync(
        request: Partial<API.GetKyujinListRequest>
    ) {
        try {
            return await ApiHelper.getBinary<ArrayBuffer>("Kyujin/GetKyujinJyokyoListForExcel", request);
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async getKyujinListForStudentAsync(
        request: Partial<API.GetKyujinListForStudentRequest>
    ): Promise<API.GetKyujinListForStudentResponse> {
        try {
            return await ApiHelper.get("Kyujin/GetKyujinListForStudent", request);
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async getKentoutyuKyujinListForStudentAsync(): Promise<API.GetKyujinListForStudentResponse> {
        try {
            return await ApiHelper.get("Kyujin/GetKentoutyuKyujinListForStudent");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async getKyujinListForSearchAsync(): Promise<API.GetKyujinListForSearchResponse> {
        try {
            return await ApiHelper.get("Kyujin/GetKyujinListForSearch");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async getByCodeAsync(code: string): Promise<API.KyujinModel> {
        try {
            return await ApiHelper.get("Kyujin/GetByCode", {
                'code': code
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteByCodeAsync(code: string) {
        try {
            return (await ApiHelper.post("Kyujin/DeleteByCode", {
                求人番号: code
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addAsync(item: API.KyujinModel): Promise<API.AddKyujinCommandResponse> {
        try {
            return (await ApiHelper.post("Kyujin/Add", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
    static async updateAsync(item: API.KyujinModel): Promise<API.UpdateKyujinCommandResponse> {
        try {
            return (await ApiHelper.post("Kyujin/Update", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }

}

export class EmailTeikeibunService {
    static async getEmailTeikeibunListAsync(): Promise<API.EmailTeikeibunModel[]> {
        try {
            return await ApiHelper.get("EmailTeikeibun/GetEmailTeikeibunList");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getEmailTeikeibunByCDAsync(EmailTeikeibunCD: string) {
        try {
            return await ApiHelper.get("EmailTeikeibun/GetEmailTeikeibunByCD", {
                "EmailTeikeibunCD": EmailTeikeibunCD
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteEmailTeikeibunAsync(EmailTeikeibunCD: string) {
        try {
            return (await ApiHelper.post("EmailTeikeibun/DeleteEmailTeikeibunByCD", {
                "メール定型文コード": EmailTeikeibunCD
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addEmailTeikeibunAsync(EmailTeikeibun: API.EmailTeikeibunModel) {
        try {
            return await ApiHelper.post("EmailTeikeibun/AddEmailTeikeibun", {
                Item: EmailTeikeibun
            });
        } catch (e) {
            throw e;
        }
    }
    static async updateEmailTeikeibunAsync(EmailTeikeibun: API.EmailTeikeibunModel) {
        try {
            return await ApiHelper.post("EmailTeikeibun/UpdateEmailTeikeibun", {
                Item: EmailTeikeibun
            });
        } catch (e) {
            throw e;
        }
    }
}

export class ReportService {
}


export class BosyuHouhouService {
    static async getBosyuHouhouListAsync(): Promise<API.BosyuHouhouModel[]> {
        try {
            return await ApiHelper.get("BosyuHouhou/GetBosyuHouhouList");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getBosyuHouhouSearchListAsync(): Promise<API.BosyuHouhouModel[]> {
        try {
            return await ApiHelper.get("BosyuHouhou/GetBosyuHouhouSearchList");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getBosyuHouhouByCodeAsync(code: string): Promise<any> {
        try {
            return await ApiHelper.get("BosyuHouhou/GetBosyuHouhouByCode", {
                'BosyuHouhouCode': code
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteBosyuHouhouByCodeAsync(code: string) {
        try {
            return (await ApiHelper.post("BosyuHouhou/DeleteBosyuHouhouByCode", {
                募集方法コード: code
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addBosyuHouhouAsync(item: API.BosyuHouhouModel) {
        try {
            return (await ApiHelper.post("BosyuHouhou/AddBosyuHouhou", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
    static async updateBosyuHouhouAsync(item: API.BosyuHouhouModel) {
        try {
            return (await ApiHelper.post("BosyuHouhou/UpdateBosyuHouhou", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
}

export class KigyoKoukaiSyubetuService {
    @logDebug()
    static async getKigyoKoukaiSyubetuListAsync() {
        return await ApiHelper.get("KigyoKoukaiSyubetu/GetKigyoKoukaiSyubetuList");

    }
    static async getKigyoKoukaiSyubetuByCodeAsync(code: string): Promise<any> {
        try {
            return await ApiHelper.get("KigyoKoukaiSyubetu/GetKigyoKoukaiSyubetuByCode", {
                'KigyoKoukaiSyubetuCode': code
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteKigyoKoukaiSyubetuByCodeAsync(code: string) {
        try {
            return (await ApiHelper.post("KigyoKoukaiSyubetu/DeleteKigyoKoukaiSyubetuByCode", {
                企業公開種別コード: code
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addKigyoKoukaiSyubetuAsync(item: API.KigyoKoukaiSyubetuModel) {
        try {
            return (await ApiHelper.post("KigyoKoukaiSyubetu/AddKigyoKoukaiSyubetu", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
    static async updateKigyoKoukaiSyubetuAsync(item: API.KigyoKoukaiSyubetuModel) {
        try {
            return (await ApiHelper.post("KigyoKoukaiSyubetu/UpdateKigyoKoukaiSyubetu", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
}

export class TodoufukenAndSikutyosonService {
    @logDebug()
    static async getTodoufukenAndSikutyosonListAsync() {
        return await ApiHelper.get("TodoufukenAndSikutyoson/GetTodoufukenAndSikutyosonList");
    }
    static async getTodoufukenAndSikutyosonByCodeAsync(code: string): Promise<API.TodoufukenAndSikutyosonModel> {
        try {
            return await ApiHelper.get("TodoufukenAndSikutyoson/GetTodoufukenAndSikutyosonByCode", {
                'DantaiCode': code
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteTodoufukenAndSikutyosonByCodeAsync(code: string) {
        try {
            return (await ApiHelper.post("TodoufukenAndSikutyoson/DeleteTodoufukenAndSikutyosonByCode", {
                団体コード: code
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addTodoufukenAndSikutyosonAsync(item: API.TodoufukenAndSikutyosonModel) {
        try {
            return (await ApiHelper.post("TodoufukenAndSikutyoson/AddTodoufukenAndSikutyoson", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
    static async updateTodoufukenAndSikutyosonAsync(item: API.TodoufukenAndSikutyosonModel) {
        try {
            return (await ApiHelper.post("TodoufukenAndSikutyoson/UpdateTodoufukenAndSikutyoson", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }

    /** 都道府県のみを取得する */
    @logDebug()
    static async getTodoufukenListAsync() {
        return await ApiHelper.get("TodoufukenAndSikutyoson/GetTodoufukenList");
    }
    static async getTodoufukenSeachListAsync() {
        return await ApiHelper.get("TodoufukenAndSikutyoson/GetTodoufukenSeachList");
    }

    /** 市区町村のみを取得する */
    @logDebug()
    static async getSikutyosonListAsync(TodoufukenCode: string) {
        return await ApiHelper.get("TodoufukenAndSikutyoson/GetSikutyosonList", {
            TodoufukenCode: TodoufukenCode
        });
    }
    static async getSikutyosonSeachListAsync(TodoufukenCode: string) {
        return await ApiHelper.get("TodoufukenAndSikutyoson/GetSikutyosonSeachList", {
            TodoufukenCode: TodoufukenCode
        });
    }
}

export class SearchConditionService {
    static async getSearchConditionListAsync(): Promise<API.SearchConditionModel[]> {
        try {
            return await ApiHelper.get("SearchCondition/GetSearchConditionList");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getSearchConditionByCodeAsync(id: number): Promise<API.SearchConditionModel> {
        try {
            return await ApiHelper.get("SearchCondition/GetSearchConditionByID", {
                'Id': id
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteSearchConditionByIdAsync(id: number) {
        try {
            return (await ApiHelper.post("SearchCondition/DeleteSearchConditionByID", {
                Id: id
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addSearchConditionAsync(item: API.SearchConditionModel): Promise<API.AddSearchConditionCommandResponse> {
        try {
            return (await ApiHelper.post("SearchCondition/AddSearchCondition", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
    static async updateSearchConditionAsync(item: API.SearchConditionModel) {
        try {
            return (await ApiHelper.post("SearchCondition/UpdateSearchCondition", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }

}

export class SearchConditionForKanrisyaService {
    @logDebug()
    static async getSearchConditionListAsync(): Promise<API.SearchConditionForKanrisyaModel[]> {
        return await ApiHelper.get("SearchConditionForKanrisya/GetSearchConditionList");

    }
    static async deleteSearchConditionByIdAsync(id: number) {
        try {
            return (await ApiHelper.post("SearchConditionForKanrisya/DeleteSearchConditionByID", {
                Id: id
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addSearchConditionAsync(item: API.SearchConditionForKanrisyaModel): Promise<API.AddSearchConditionCommandResponse> {
        try {
            return (await ApiHelper.post("SearchConditionForKanrisya/AddSearchCondition", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
    static async updateSearchConditionAsync(item: API.SearchConditionForKanrisyaModel) {
        try {
            return (await ApiHelper.post("SearchConditionForKanrisya/UpdateSearchCondition", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }

}


export class KentoutyuService {
    static async getKentoutyuListAsync(): Promise<API.KentoutyuModel[]> {
        try {
            return await ApiHelper.get("Kentoutyu/GetKentoutyuList");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getKentoutyuByCodeAsync(id: number): Promise<API.KentoutyuModel> {
        try {
            return await ApiHelper.get("Kentoutyu/GetKentoutyuByID", {
                'Id': id
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteKentoutyuByIdAsync(id: number) {
        try {
            return (await ApiHelper.post("Kentoutyu/DeleteKentoutyuByID", {
                Id: id
            }));
        } catch (e) {
            throw e;
        }
    }
    static async deleteKentoutyuByModelAsync(item: API.KentoutyuModel) {
        try {
            return (await ApiHelper.post("Kentoutyu/DeleteKentoutyuByModel", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addKentoutyuAsync(item: API.KentoutyuModel) {
        try {
            return (await ApiHelper.post("Kentoutyu/AddKentoutyu", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
    static async updateKentoutyuAsync(item: API.KentoutyuModel) {
        try {
            return (await ApiHelper.post("Kentoutyu/UpdateKentoutyu", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
}
export class KigyoService {

    static async getKigyoListAsync(
        request: Partial<API.GetKigyoListRequest>
    ): Promise<API.GetKigyoListResponse> {
        try {
            return await ApiHelper.get("Kigyo/GetKigyoList", request);
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async getKigyoListBySitenAsync(request: Partial<API.GetKigyoListBySitenRequest>): Promise<API.GetKigyoListBySitenResponse> {
        try {
            return await ApiHelper.get("Kigyo/GetKigyoListBySiten", request);
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }


    static async getKigyoListForSearchAsync(): Promise<API.KigyoForSearchModel[]> {
        try {
            return await ApiHelper.get("Kigyo/GetKigyoListForSearch");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async getKigyoListForKigyoEditListAsync(): Promise<API.KigyoForKigyoEditListModel[]> {
        try {
            return await ApiHelper.get("Kigyo/GetKigyoListForKigyoEditList");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async getByCodeAsync(code: string): Promise<API.KigyoModel> {
        try {
            return await ApiHelper.get("Kigyo/GetByCode", {
                'code': code
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteByCodeAsync(code: string) {
        try {
            return (await ApiHelper.post("Kigyo/DeleteByCode", {
                企業コード: code
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addAsync(item: API.KigyoModel): Promise<API.AddKigyoCommandResponse> {
        try {
            return (await ApiHelper.post("Kigyo/Add", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
    static async updateAsync(item: API.KigyoModel) {
        try {
            return (await ApiHelper.post("Kigyo/Update", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }

}

export class KigyoMeisaiService {

    static async getKigyoMeisaiListAsync(
        request: Partial<API.GetKigyoMeisaiListRequest>
    ): Promise<API.GetKigyoMeisaiListResponse> {
        try {
            return await ApiHelper.get("KigyoMeisai/GetKigyoMeisaiList", request);
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async getKigyoMeisaiListForSearchAsync(kigyoCode?: string): Promise<API.KigyoMeisaiForSearchModel[]> {
        try {
            return await ApiHelper.get("KigyoMeisai/GetKigyoMeisaiListForSearch", {
                kigyoCode
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async getByCodeAsync(code: string, honsitenCode: number): Promise<API.KigyoMeisaiModel> {
        try {
            return await ApiHelper.get("KigyoMeisai/GetByCode", {
                'code': code,
                "HonsitenCode": honsitenCode
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteByCodeAsync(code: string) {
        try {
            return (await ApiHelper.post("KigyoMeisai/DeleteByCode", {
                得意先コード: code
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addAsync(item: API.KigyoMeisaiModel) {
        try {
            return (await ApiHelper.post("KigyoMeisai/Add", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
    static async updateAsync(item: API.KigyoMeisaiModel) {
        try {
            return (await ApiHelper.post("KigyoMeisai/Update", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }

}

export class KigyoSyosaiKinmutiService {

    static async getKigyoSyosaiKinmutiListAsync(
        request: Partial<API.GetKigyoSyosaiKinmutiListRequest>
    ): Promise<API.GetKigyoSyosaiKinmutiListResponse> {
        try {
            return await ApiHelper.get("KigyoSyosaiKinmuti/GetKigyoSyosaiKinmutiList", request);
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async getByCodeAsync(code: string, honsitenCode: number, kigyoSyosaiKinmutiCode: string): Promise<API.KigyoSyosaiKinmutiModel> {
        try {
            return await ApiHelper.get("KigyoSyosaiKinmuti/GetByCode", {
                'kigyoCode': code,
                'HonsitenCode': honsitenCode,
                'kigyoSyosaiKinmutiCode': kigyoSyosaiKinmutiCode
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

    static async deleteByCodeAsync(code: string, honsitenCode: number, kigyoSyosaiKinmutiCode: string) {
        try {
            return (await ApiHelper.post("KigyoSyosaiKinmuti/DeleteByCode", {
                '企業コード': code,
                '本支店コード': honsitenCode,
                '企業詳細勤務地コード': kigyoSyosaiKinmutiCode
            }));
        } catch (e) {
            throw e;
        }
    }

    static async addAsync(item: API.KigyoSyosaiKinmutiModel) {
        try {
            return (await ApiHelper.post("KigyoSyosaiKinmuti/Add", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
    static async updateAsync(item: API.KigyoSyosaiKinmutiModel) {
        try {
            return (await ApiHelper.post("KigyoSyosaiKinmuti/Update", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
}

export class DownloadKoumokuService {
    @logDebug()
    static async getDownloadKoumokuSetteiNameListAsync(): Promise<API.DownloadKoumokuSetteiNameModel[]> {
        return await ApiHelper.get("DownloadKoumoku/GetDownloadKoumokuSetteiNameList", {});
    }
    static async getDownloadKoumokuByNameAsync(setteiName: string): Promise<API.DownloadKoumokuEditModel> {
        try {
            return await ApiHelper.get("DownloadKoumoku/GetDownloadKoumokuByName", {
                "setteiName": setteiName
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteDownloadKoumokuAsync(setteiName: string) {
        try {
            return (await ApiHelper.post("DownloadKoumoku/DeleteDownloadKoumokuByName", {
                "ダウンロード設定名": setteiName,
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addDownloadKoumokuAsync(settei: API.DownloadKoumokuEditModel) {
        try {
            return await ApiHelper.post("DownloadKoumoku/AddDownloadKoumoku", {
                Item: settei
            });
        } catch (e) {
            throw e;
        }
    }
    static async updateDownloadKoumokuAsync(settei: API.DownloadKoumokuEditModel) {
        try {
            return await ApiHelper.post("DownloadKoumoku/UpdateDownloadKoumoku", {
                Item: settei
            });
        } catch (e) {
            throw e;
        }
    }
    @logDebug()
    static async getDefaultDownloadKoumokuItemsAsync(): Promise<API.DownloadKoumokuModel[]> {
        return await ApiHelper.get("DownloadKoumoku/GetDefaultDownloadKoumokuItems", {})
    }

}

export class KyujinPdfService {
    static async getUnRegisteredPdfList(): Promise<string[]> {
        try {
            return (await ApiHelper.get("KyujinPdf/GetUnRegisteredPdfList"));
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getPdfUrl(path: string): Promise<string | null> {
        try {
            return (await ApiHelper.get("KyujinPdf/GetPdfUrl", {
                path: path
            }));
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async Delete(path: string): Promise<API.MoveFileResponse> {
        try {
            return (await ApiHelper.get("KyujinPdf/Delete", {
                path: path
            }));
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async downloadPdf(path: string, fileDownloadName: string): Promise<string | null> {
        try {
            return (await ApiHelper.get("KyujinPdf/DownloadPdf", {
                path: path,
                fileDownloadName: fileDownloadName
            }));
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
}

export class MailAddressListService {
    static async getMailAddressListListAsync(): Promise<API.MailAddressListModel[]> {
        try {
            return await ApiHelper.get("MailAddressList/GetMailAddressListList");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getMailAddressListByCodeAsync(code: string): Promise<API.MailAddressListModel[]> {
        try {
            return await ApiHelper.get("MailAddressList/GetMailAddressListByCode", {
                'MailAddressListCode': code
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    /**
     * リストに登録されているユーザーの情報を返します。
     * @param code 
     * @returns 
     */
    static async getMailAddressListUserByCodeAsync(code: string): Promise<API.UserModel[]> {
        try {
            return await ApiHelper.get("MailAddressList/GetMailAddressListUserByCode", {
                'MailAddressListCode': code
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteMailAddressListByCodeAsync(code: string) {
        try {
            return (await ApiHelper.post("MailAddressList/DeleteMailAddressListByCode", {
                メール宛先リストコード: code
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addMailAddressListAsync(item: API.MailAddressListModel): Promise<API.AddMailAddressListCommandResponse> {
        try {
            return (await ApiHelper.post("MailAddressList/AddMailAddressList", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
    static async updateMailAddressListAsync(item: API.MailAddressListModel) {
        try {
            return (await ApiHelper.post("MailAddressList/UpdateMailAddressList", {
                Item: item
            }));
        } catch (e) {
            throw e;
        }
    }
}

export class MailFromAddressListService {
    static async getMailSettingListAsync(): Promise<API.MailSettingModel[]> {
        try {
            return await ApiHelper.get("MailSetting/GetMailSettingList");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
}


export class MailService {
    static async SendMailToAddressListAsync(request: API.SendMailToAddressListRequest): Promise<void> {
        try {
            return await ApiHelper.post("Mail/SendMailToAddressList", request);
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async SendTaskMailAsync(): Promise<API.SendMailDelivaryTaskAllCommandResponse> {
        try {
            return await ApiHelper.get("Mail/SendTaskMail", {});
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async CreateMailFromSearchCondition(): Promise<API.AddMailDelivaryTaskFromKyujinCommandResponse> {
        try {
            return await ApiHelper.get("Mail/CreateMailFromSearchCondition", {});
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getMailSendListAsync(
        request: Partial<API.GetMailSendListRequest>
    ): Promise<API.GetMailSendListResponse> {
        try {
            return await ApiHelper.get("Mail/GetMailSendList", request);
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }

}

export class SummaryService {
    @logDebug()
    static async GetSummaryBuJiyuKoumoku(request: Partial<API.GetSummaryByJiyuKoumokuRequest>): Promise<API.GetSummaryByJiyuKoumokuResponse> {
        return await ApiHelper.get("Summary/GetSummaryByJiyuKoumoku", request)
    }

    @logDebug()
    static async GetSummary(request: API.GetSummaryRequest): Promise<API.GetSummaryResponse> {
        return await ApiHelper.get("Summary/GetSummary", request)
    }

    @logDebug()
    static async GetSummaryForExcelAsync(request: API.GetSummaryRequest) {
        return await ApiHelper.getBinary<ArrayBuffer>("Summary/GetSummaryForExcel", request);
    }

}

export class KyujinBikouTeikeibunService {
    static async getKyujinBikouTeikeibunListAsync(): Promise<API.EmailTeikeibunModel[]> {
        try {
            return await ApiHelper.get("KyujinBikouTeikeibun/GetKyujinBikouTeikeibunList");
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async getKyujinBikouTeikeibunByCDAsync(BikouTeikeibunCD: string): Promise<API.KyujinBikouTeikeibunModel> {
        try {
            return await ApiHelper.get("KyujinBikouTeikeibun/GetKyujinBikouTeikeibunByCD", {
                "KyujinBikouTeikeibunCD": BikouTeikeibunCD
            });
        } catch (e) {
            Logger.logDebug(e.message);
            throw e;
        }
    }
    static async deleteKyujinBikouTeikeibunAsync(BikouTeikeibunCD: string) {
        try {
            return (await ApiHelper.post("KyujinBikouTeikeibun/DeleteKyujinBikouTeikeibunByCD", {
                "備考定型文コード": BikouTeikeibunCD
            }));
        } catch (e) {
            throw e;
        }
    }
    static async addKyujinBikouTeikeibunAsync(KyujinBikouTeikeibun: API.KyujinBikouTeikeibunModel) {
        try {
            return await ApiHelper.post("KyujinBikouTeikeibun/AddKyujinBikouTeikeibun", {
                Item: KyujinBikouTeikeibun
            });
        } catch (e) {
            throw e;
        }
    }
    static async updateKyujinBikouTeikeibunAsync(KyujinBikouTeikeibun: API.KyujinBikouTeikeibunModel) {
        try {
            return await ApiHelper.post("KyujinBikouTeikeibun/UpdateKyujinBikouTeikeibun", {
                Item: KyujinBikouTeikeibun
            });
        } catch (e) {
            throw e;
        }
    }
}
